import { FlexContainerColumn, H5, H6, Large } from '@demandstar/components/styles';
import { DSEmail } from '@demandstar/components/email';

import { CartItemList, CartWrapper } from './styles';
import { commonEmail, TollFreeNumber } from 'src/utils/texts/common';
import { useCart, useRefreshCartTotal } from '.';

import { CartAlert } from './CartAlert';
import { CartItem } from './CartItem';
import { displayDate } from 'src/utils/helpers';
import { ProductsCopy } from 'src/utils/texts/supplier/subscription';
import { calculateServiceCharge } from '../helpers';
import { useDSSelector } from '../../../store/reducers';

interface CartProps {
  /** indicates if this page is used for review */
  isReview?: boolean;
  /** indicates whether the delete icon should be displayed for cart line items */
  showDelIcon?: boolean;
}

/**
 * @description A cart used for selecting products & checking out
 * @default { isReview = false, showDelIcon = false }
 * @returns JSX.Element
 *
 * @example <Cart isReview={false} />
 */
export const Cart = ({ isReview = false, showDelIcon = true }: CartProps) => {
  useRefreshCartTotal();
  const productPrices = useDSSelector(state => state.shared?.productPrices) || { serviceFeePercentage: 0 };
  const {
    cartItems,
    cartRenewal,
    cartTotal,
    discountedCartTotal,
    expiryDate,
    maximumCartDiscount,
    recurringTotal,
    showCart,
  } = useCart();

  return (
    <CartWrapper isProrated={recurringTotal !== cartTotal}>
      {showCart && (
        <H5>{cartRenewal ? ProductsCopy.CartRenewalHeader : ProductsCopy.CartHeader}</H5>
      )}
      <CartItemList>
        {cartItems.map(ci => (
          <CartItem
            key={ci.productId}
            product={ci}
            isRemovable={showDelIcon && !isReview && !cartRenewal}
          />
        ))}
      </CartItemList>

      <H6 lineheight="0.5rem" marginTop='1rem'>
        {ProductsCopy.CartTotalcharge}
        <span data-testid="product-total"className='pr-5'>
          ${discountedCartTotal.toFixed(2)}
        </span>
      </H6>
{calculateServiceCharge(productPrices.serviceFeePercentage, discountedCartTotal) > 0 &&
      <H6 lineheight="0.5rem" marginTop='0.1rem'>
        {ProductsCopy.ServiceCharge}{`(${productPrices.serviceFeePercentage}%) :`}
        <span data-testid="service-charge" className='pr-5'>
          ${calculateServiceCharge(productPrices.serviceFeePercentage, discountedCartTotal).toFixed(2)}
        </span>
      </H6>}
      <H6>
        {ProductsCopy.CartTotal}
        <span data-testid="cart-total" className='pr-5' >
          $
          {
            (discountedCartTotal + calculateServiceCharge(productPrices.serviceFeePercentage, discountedCartTotal)
            ).toFixed(2)
          }

          {maximumCartDiscount > 0 ? ' *' : ''}
        </span>
      </H6>

      <FlexContainerColumn>
        {recurringTotal > 0 && recurringTotal !== cartTotal && (
          <>
            <Large>
              {`${discountedCartTotal <= 0
                  ? 'If you complete this change, your account will renew on'
                  : ProductsCopy.RenewsOn
                } ${displayDate(expiryDate)} for $${recurringTotal + calculateServiceCharge(productPrices.serviceFeePercentage, recurringTotal)
                } (included ${productPrices.serviceFeePercentage} percent service charge).`}
            </Large>

            {!cartRenewal && <Large>{ProductsCopy.IncludesCurrentAndAdded}</Large>}

            {discountedCartTotal <= 0 && (
              <Large>
                If you would like to proceed with this change, please{' '}
                <DSEmail address={commonEmail.support}>email us</DSEmail> or call us at{' '}
                {TollFreeNumber}
              </Large>
            )}
          </>
        )}
        {!isReview && <CartAlert />}

        {maximumCartDiscount > 0 && (
          <Large>
            {`* Your total today includes a $${(discountedCartTotal === 0
              ? cartTotal
              : maximumCartDiscount
            ).toFixed(2)}
              discount for previously subscribed products.`}
          </Large>
        )}
      </FlexContainerColumn>
    </CartWrapper>
  );
};
